<template>
    <StepShell :embedded="embedded">
        <StepHeader :embedded="embedded" align="center" :title="t('onboarding.calendarFirst.profileIntroStep.title')" :lede="ledeCopy" :source="embedded ? 'profile' : undefined" />

        <NadiaCard :title="t('onboarding.calendarFirst.profileIntroStep.cardTitle')">
            <div>
                <p class="card-body">{{ t("onboarding.calendarFirst.profileIntroStep.cardBody") }}</p>
                <StepPoints :points="points" variant="dot" />
            </div>

            <div class="compare">
                <article v-for="panel in panels" :key="panel.id" class="panel" :class="{ 'is-with': panel.id === 'with' }">
                    <p class="panel-label">{{ panel.label }}</p>
                    <p class="panel-question">{{ panelQuestion }}</p>
                    <p class="panel-answer">{{ panel.answer }}</p>
                    <p class="panel-note">{{ panel.note }}</p>
                </article>
            </div>
            <p class="compare-caption">{{ t("onboarding.calendarFirst.profileIntroStep.panels.caption") }}</p>
        </NadiaCard>

        <StepActions flush>
            <BaseButton v-if="skippable ?? !embedded" theme="tertiary" class="step-skip" :disabled="busy" @click="emit('skip')">{{
                t("onboarding.calendarFirst.actions.skipForNow")
            }}</BaseButton>
            <BaseButton theme="primary" :disabled="busy" @click="emit('continue')">{{
                draftReady ? t("onboarding.calendarFirst.profileIntroStep.ctaReady") : t("onboarding.calendarFirst.profileIntroStep.cta")
            }}</BaseButton>
        </StepActions>
    </StepShell>
</template>

<script setup lang="ts">
/**
 * Explain the profile and its audience before asking users to approve a draft. Reuse the profile-creation landing's
 * comparison without its staged animation because this screen sits inside onboarding.
 */
import BaseButton from "~vue/components/BaseButton.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import NadiaCard from "./NadiaCard.vue";
import StepActions from "./StepActions.vue";
import StepHeader from "./StepHeader.vue";
import type { StepPoint } from "./StepPoints.vue";
import StepPoints from "./StepPoints.vue";
import StepShell from "./StepShell.vue";

const {
    firstName,
    managesPeople,
    embedded = false,
    skippable = undefined,
    busy = false,
    draftReady = false,
} = defineProps<{
    firstName: string;
    managesPeople: boolean;
    embedded?: boolean;
    skippable?: boolean;
    busy?: boolean;
    /** A background run already wrote the draft, so continuing opens it rather than writing one. */
    draftReady?: boolean;
}>();

const emit = defineEmits<{ continue: []; skip: [] }>();

const { t } = useI18n();

const ledeCopy = computed(() => (managesPeople ? t("onboarding.calendarFirst.profileIntroStep.lede.manager") : t("onboarding.calendarFirst.profileIntroStep.lede.ic")));

const points = computed<StepPoint[]>(() => [
    { tone: "pink", lead: t("onboarding.calendarFirst.profileIntroStep.points.draft") },
    { tone: "amber", lead: t("onboarding.calendarFirst.profileIntroStep.points.review") },
    { tone: "teal", lead: t("onboarding.calendarFirst.profileIntroStep.points.private") },
]);

const panelQuestion = computed(() =>
    firstName ? t("onboarding.calendarFirst.profileIntroStep.panels.question", { name: firstName }) : t("onboarding.calendarFirst.profileIntroStep.panels.questionAnonymous"),
);

const panels = computed(() => [
    {
        id: "without",
        label: t("onboarding.calendarFirst.profileIntroStep.panels.without.label"),
        answer: t("onboarding.calendarFirst.profileIntroStep.panels.without.answer"),
        note: t("onboarding.calendarFirst.profileIntroStep.panels.without.note"),
    },
    {
        id: "with",
        label: t("onboarding.calendarFirst.profileIntroStep.panels.with.label"),
        answer: t("onboarding.calendarFirst.profileIntroStep.panels.with.answer"),
        note: t("onboarding.calendarFirst.profileIntroStep.panels.with.note"),
    },
]);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .card-body {
        @apply body-small text-ink-muted;
    }

    /*
     * Keep panels side by side when space permits so the second answer reads as an alternative rather than
     * a correction.
     */
    .compare {
        @apply grid grid-cols-1 gap-3 sm:grid-cols-2;
    }

    .panel {
        @apply flex flex-col gap-2 rounded-lgr bg-surface-sunken p-4;
    }

    .panel.is-with {
        @apply bg-chip-nadia-bg/50;
    }

    .panel-label {
        @apply scaffold-details-small text-ink-muted;
    }

    .panel-question {
        @apply body-small rounded-lgr bg-surface-raised px-3 py-2 text-ink;
    }

    .panel-answer {
        @apply nadia-chat text-ink;
    }

    .panel-note {
        @apply details mt-auto pt-1 text-ink-muted;
    }

    .compare-caption {
        @apply details text-ink-muted;
    }
}
</style>
